/**
 * useTaskNodeStyles - Style and class name computation for TaskNode
 */

import { useMemo } from 'react';
import { CATEGORY_COLORS } from '../constants';
import { getPrimaryTag } from '../../../hooks/useTagActions';
// Default and min dimensions
export const DEFAULT_WIDTH = 320;
export const DEFAULT_HEIGHT = 162;
export const MIN_WIDTH = 220;
export const MIN_HEIGHT = 112;

export function useTaskNodeStyles({
  task,
  allCategories,
  allTags,
  nestedSprint,
  statuses = [],
  lockState,
  isSelected,
  isDragging,
  isResizing,
  isConnecting,
  isConnectionTarget,
  isConnectionHovered,
  isFocusedMain,
  isFocusDependency,
  isAnimating,
  isFadingOut,
  isFadingIn,
  isDropTarget,
  isHoveredFromHUD,
  isMultiCampaignMode,
}) {
  const isLocked = lockState?.isLocked && task.status !== 'completed';
  const isExcluded = lockState?.isExcluded;
  const isBlocked = task.status === 'blocked';
  const isEffectivelyCompleted = task.status === 'completed' || (nestedSprint && nestedSprint.progress === 100);

  // Get primary tag for styling
  const primaryTagName = getPrimaryTag(task);
  const availableTags = allTags.length > 0 ? allTags : allCategories;
  const primaryTagObject = useMemo(() => {
    if (!primaryTagName) return null;
    return availableTags.find(t => t.name === primaryTagName) || { name: primaryTagName, color: CATEGORY_COLORS[0] };
  }, [primaryTagName, availableTags]);
  
  const currentCategory = primaryTagObject;

  // Get source campaign info for multi-campaign mode
  const sourceCampaign = task.sourceCampaign;
  const hasSourceCampaign = isMultiCampaignMode && sourceCampaign;

  // Get custom size or use defaults
  const nodeWidth = task.width || DEFAULT_WIDTH;
  const nodeHeight = task.height || DEFAULT_HEIGHT;

  // Build class names
  const nodeClasses = useMemo(() => {
    return [
      'task-node',
      isSelected && 'task-node--selected',
      isDragging && 'task-node--dragging',
      isResizing && 'task-node--resizing',
      isConnecting && 'task-node--connecting',
      isConnectionTarget && 'task-node--target',
      isConnectionHovered && 'task-node--target-hovered',
      isFocusedMain && 'task-node--focused-main',
      isFocusDependency && 'task-node--focus-dependency',
      isAnimating && 'task-node--animating',
      isFadingOut && 'task-node--fading-out',
      isFadingIn && 'task-node--fading-in',
      isDropTarget && 'task-node--drop-target',
      isEffectivelyCompleted && 'task-node--completed',
      isExcluded && 'task-node--excluded',
      isLocked && 'task-node--locked',
      isBlocked && 'task-node--blocked',
      currentCategory && 'task-node--has-category',
      hasSourceCampaign && 'task-node--multi-campaign',
      isHoveredFromHUD && 'task-node--hud-hover',
    ].filter(Boolean).join(' ');
  }, [
    isSelected, isDragging, isResizing, isConnecting, isConnectionTarget,
    isConnectionHovered, isFocusedMain, isFocusDependency, isAnimating, isFadingOut, isFadingIn,
    isDropTarget, isEffectivelyCompleted, isExcluded, isLocked, isBlocked, currentCategory,
    hasSourceCampaign, isHoveredFromHUD,
  ]);

  // Build inline styles
  const nodeStyles = useMemo(
    () => ({
      /* Status pill sets its own --status-*; no status tint on the node shell */
      '--category-color': currentCategory?.color || 'transparent',
      '--category-bg': currentCategory ? `${currentCategory.color}18` : 'transparent',
      '--category-border': currentCategory ? `${currentCategory.color}35` : 'var(--border-color)',
      '--campaign-color': hasSourceCampaign ? sourceCampaign.color : 'transparent',
    }),
    [currentCategory, hasSourceCampaign, sourceCampaign]
  );

  return {
    isLocked,
    isExcluded,
    isBlocked,
    primaryTagObject,
    currentCategory,
    availableTags,
    sourceCampaign,
    hasSourceCampaign,
    nodeWidth,
    nodeHeight,
    nodeClasses,
    nodeStyles,
  };
}

export default useTaskNodeStyles;
